import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { Markdown } from 'tiptap-markdown';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Heading3,
  Heading4,
  List,
  ListOrdered,
  Link2,
  Table as TableIcon,
  Rows3,
  Columns3,
  Trash2,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * A visual (what-you-see) editor for one tech-spec section — so a reviewer who doesn't know
 * markdown can edit prose, headings, bullet/numbered lists, links and tables with toolbar
 * buttons instead of typing `**`, `#`, `|` or ``` fences. TipTap holds the document; on finish
 * we serialize it back to markdown (the agent's source of truth) via tiptap-markdown.
 *
 * Fidelity guarantee: loading then re-serializing an UNEDITED section is byte-identical for the
 * constructs these specs contain — headings, GFM tables, JSON code fences, nested lists, figure
 * blockquotes — verified in specMarkdownRoundtrip.test.ts. The converter's own table spelling
 * (`|-------|`, padded cells) is NOT byte-identical, so an update whose content still serializes
 * like the untouched section keeps the original text (see untouchedMdRef). Only what the
 * reviewer actually changes is rewritten.
 */

// Allowed link schemes — reject javascript:/data:/vbscript: etc. so a link href can't carry an
// XSS payload. Enforced in three places: setLink (typed links), the Link extension `protocols`
// (autolink / paste), and stripUnsafeMarkdownLinks (hrefs tiptap-markdown parses directly, which
// the extension does NOT filter).
const SAFE_LINK_PROTOCOLS = ['http', 'https', 'mailto'];

// A markdown link `[text](scheme:…)` whose scheme is dangerous — but NOT an image `![alt](…)`
// (leading `!`), which isn't an href-click vector. The URL body allows one level of balanced
// parens (e.g. `javascript:alert(1)`) so the whole link is consumed. Neutralized to plain text.
const UNSAFE_MD_LINK_RE =
  /(?<!!)\[([^\]]*)\]\(\s*(?:javascript|data|vbscript|file):(?:[^()]|\([^()]*\))*\)/gi;

/** Drop dangerous-scheme link targets from markdown, keeping the link text. Applied to the
 *  editor's initial content so a pre-existing `[x](javascript:…)` never becomes a clickable
 *  `<a>` in the contenteditable (react-markdown already sanitizes the read view; the PDF can't
 *  execute JS). */
export function stripUnsafeMarkdownLinks(md: string): string {
  return md.replace(UNSAFE_MD_LINK_RE, '$1');
}

// Shared with the round-trip guard test so the test exercises the exact editor schema.
export const SECTION_EDITOR_EXTENSIONS = [
  // Only allow safe schemes for autolinked / pasted links (typed links are validated in setLink;
  // markdown-parsed hrefs are handled by stripUnsafeMarkdownLinks on load).
  StarterKit.configure({ link: { protocols: SAFE_LINK_PROTOCOLS } }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  // html:false keeps the doc to markdown constructs only (no raw HTML injection) — this is the
  // config the round-trip fidelity was verified under.
  Markdown.configure({ html: false }),
];

/** Serialize a TipTap editor's document back to markdown. */
export function serializeToMarkdown(editor: Editor): string {
  // tiptap-markdown attaches getMarkdown() under storage.markdown; its type is loose.
  const storage = editor.storage as { markdown?: { getMarkdown: () => string } };
  return storage.markdown?.getMarkdown() ?? '';
}

/** Serialize any document of this editor (e.g. a transaction's "before") to markdown. */
function serializeDocToMarkdown(editor: Editor, doc: ProseMirrorNode): string {
  const storage = editor.storage as {
    markdown?: { serializer: { serialize: (content: ProseMirrorNode) => string } };
  };
  return storage.markdown?.serializer.serialize(doc) ?? '';
}

// Content styling for the editable surface. Mirrors SpecMarkdown's read view (light code blocks,
// bordered/zebra tables, navy headings) so editing looks like the rendered result. All Tailwind
// utilities / arbitrary variants — no CSS-in-CSS.
const EDITOR_CLASS = [
  'prose prose-sm max-w-none focus:outline-none px-3.5 py-3 min-h-[140px] text-[13.5px] leading-[1.7] text-ink',
  '[&_h1]:text-navy [&_h2]:text-navy [&_h3]:text-navy [&_h4]:text-navy [&_strong]:text-navy',
  '[&_code]:before:content-none [&_code]:after:content-none',
  // Tailwind preflight resets `a` to inherit colour AND decoration, so a link was
  // indistinguishable from body text — the reviewer couldn't tell it had worked.
  '[&_a]:text-electric [&_a]:underline [&_a]:underline-offset-2',
  '[&_pre]:bg-[#fbfbfc] [&_pre]:text-ink [&_pre]:border [&_pre]:border-line [&_pre]:rounded-md [&_pre]:text-[12px]',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse',
  // A long unbroken value (an ID, a hash, a pasted blob) used to force the cell wider than the
  // card and push the whole table out of view — the read view already avoids this by wrapping
  // tables in a scroller and breaking words, so the editor was the odd one out. `anywhere`, not
  // `break-word`: only `anywhere` reduces the cell's min-content width, which is what lets the
  // column shrink back instead of overflowing. The scroller on TipTap's own .tableWrapper is the
  // backstop for tables that are genuinely too wide (many columns) rather than long-worded.
  '[&_.tableWrapper]:overflow-x-auto',
  '[&_th]:border [&_th]:border-line [&_th]:bg-line-soft [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:[overflow-wrap:anywhere]',
  '[&_td]:border [&_td]:border-line-soft [&_td]:px-2 [&_td]:py-1 [&_td]:align-top [&_td]:[overflow-wrap:anywhere]',
  '[&_.selectedCell]:bg-electric/10',
].join(' ');

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      // Keep the editor selection while clicking a formatting button (mousedown would otherwise
      // blur the contenteditable and drop the selection the command acts on).
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-navy transition-colors',
        'hover:bg-line-soft disabled:opacity-40 disabled:hover:bg-transparent',
        active && 'bg-electric/10 text-electric',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-line-soft" aria-hidden />;
}

/** Word/Docs-style grid picker: hover to choose the table size, click to insert. Lives inside
 *  the editor wrapper (not a portal) so opening it never triggers the wrapper's blur-commit. */
function TableSizePicker({ onPick }: { onPick: (rows: number, cols: number) => void }) {
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const MAX = 8;
  return (
    <div
      className="absolute left-0 top-full z-20 mt-1 rounded-lg border border-line bg-white p-2 shadow-card"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="mb-1.5 text-center text-[11px] font-semibold text-navy">
        {hover.r + 1} {hover.r === 0 ? 'row' : 'rows'} × {hover.c + 1} {hover.c === 0 ? 'column' : 'columns'}
      </div>
      <div className="grid w-max grid-cols-8 gap-1">
        {Array.from({ length: MAX * MAX }).map((_, i) => {
          const r = Math.floor(i / MAX);
          const c = i % MAX;
          const on = r <= hover.r && c <= hover.c;
          return (
            <button
              key={i}
              type="button"
              aria-label={`${r + 1} by ${c + 1}`}
              onMouseEnter={() => setHover({ r, c })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(r + 1, c + 1)}
              className={cn('size-4 rounded-[2px] border', on ? 'border-electric bg-electric/30' : 'border-line')}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * @param value    initial markdown (used only to seed the editor; the editor is the source of
 *                 truth while open — the prop is not re-applied on change, so a parent re-render
 *                 can't reset the cursor mid-edit).
 * @param onCommit called with the serialized markdown when the reviewer finishes (Done, or
 *                 clicking outside the editor). Guarded so an unchanged section never commits.
 * @param onDone   asks the parent to leave edit mode (fired alongside the Done commit).
 */
export function VisualSectionEditor({
  value,
  onCommit,
  onDone,
  autoFocus,
}: {
  value: string;
  onCommit: (markdown: string) => void;
  onDone: () => void;
  autoFocus?: boolean;
}) {
  // Latest serialized markdown, kept fresh on every edit so commit-on-unmount can use the ref
  // instead of the (by-then possibly torn-down) editor instance.
  const latestMdRef = useRef(value);
  // How the editor itself writes the UNTOUCHED section. Converter tables (`|-------|`, padded
  // cells) come back as `| --- |`, and focusing a section that ends in a table fires an update
  // that adds only an empty trailing paragraph — so an update alone is not an edit (QA
  // 2026-10-09: opening "Document Control" and clicking out saved, toasted and audited an edit
  // nobody made, and marked every market "updated"). Content that still serializes to this
  // is the original, kept byte-for-byte.
  const untouchedMdRef = useRef<string | null>(null);
  // The text the section OPENED with. Not the live `value` prop: useEditor calls the newest
  // onUpdate, and a parent that saves on commit passes the saved edit back in — reverting to
  // the original would then "equal" the edit and never save (audit 2026-10-11).
  const seedRef = useRef(value);
  const editor = useEditor({
    extensions: SECTION_EDITOR_EXTENSIONS,
    content: stripUnsafeMarkdownLinks(value),
    autofocus: autoFocus ? 'end' : false,
    editorProps: { attributes: { class: EDITOR_CLASS } },
    onUpdate: ({ editor, transaction }) => {
      // The first update's "before" document is the untouched section.
      untouchedMdRef.current ??= serializeDocToMarkdown(editor, transaction.before);
      const md = serializeToMarkdown(editor);
      latestMdRef.current = md === untouchedMdRef.current ? seedRef.current : md;
    },
  });

  // Track what was last committed so we never save the same content twice — every commit writes
  // an audit row, and blur (click-away) + Done can both fire with nothing changed between them.
  // Seeds from the initial markdown, so an unedited section never commits at all.
  const lastCommitted = useRef(value);
  const [tablePicker, setTablePicker] = useState(false);
  // Link dialog: null = closed. Replaces window.prompt, which is unstyled, blocks the page,
  // and on some browsers is suppressed entirely — the reviewer then had no way to add a link.
  const [linkDraft, setLinkDraft] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  // The dialog renders in a portal, so opening it moves focus out of the editor wrapper and
  // would fire the click-away commit. Harmless (commit only saves) but it also surrenders the
  // selection the link is about to be applied to, so hold it while the dialog is open.
  const linkOpenRef = useRef(false);

  // Commit any pending change if the section unmounts without a blur/Done (SPA navigation, the
  // row collapsing). Uses latestMdRef so it works after TipTap tears the editor down; the
  // lastCommitted guard dedupes the normal Done/blur case (no double save).
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  useEffect(
    () => () => {
      if (latestMdRef.current !== lastCommitted.current) {
        lastCommitted.current = latestMdRef.current;
        onCommitRef.current(latestMdRef.current);
      }
    },
    [],
  );

  if (!editor) return null;

  const commit = () => {
    // Use latestMdRef (only advanced by onUpdate, i.e. an actual user edit) rather than
    // re-serializing the editor. So opening a section and leaving WITHOUT editing can never
    // rewrite it — even if a markdown construct doesn't round-trip byte-for-byte through TipTap.
    const md = latestMdRef.current;
    if (md !== lastCommitted.current) {
      lastCommitted.current = md;
      onCommit(md);
    }
  };

  const openLinkDialog = () => {
    linkOpenRef.current = true;
    setLinkError(null);
    setLinkDraft((editor.getAttributes('link').href as string) || '');
  };

  const closeLinkDialog = () => {
    linkOpenRef.current = false;
    setLinkDraft(null);
    setLinkError(null);
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLinkDialog();
  };

  const applyLink = (raw: string) => {
    if (raw === '') {
      removeLink();
      return;
    }
    // Validate the scheme before inserting — reject javascript:/data:/vbscript: (XSS via href).
    // A schemeless entry (e.g. "example.com") is treated as https. Belt-and-suspenders with the
    // Link extension's `protocols` config above.
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      try {
        parsed = new URL(`https://${raw}`);
      } catch {
        setLinkError('That doesn’t look like a valid link.');
        return;
      }
    }
    if (!SAFE_LINK_PROTOCOLS.includes(parsed.protocol.replace(/:$/, ''))) {
      setLinkError('Links must start with http, https or mailto.');
      return;
    }
    const href = parsed.toString();
    // NOTHING SELECTED: `setLink` only marks the current selection, so with a bare cursor it
    // applied the link to an empty range and the reviewer saw the address silently disappear.
    // Insert the address AS the link text instead — what every other editor does. (A cursor
    // sitting inside an existing link is not this case: extendMarkRange finds that link's
    // range, so editing an existing link still works through the branch below.)
    const { empty } = editor.state.selection;
    if (empty && !editor.isActive('link')) {
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] })
        // Leave the link mark behind, or everything typed after the link joins it.
        .unsetMark('link')
        .run();
      closeLinkDialog();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    closeLinkDialog();
  };

  const inTable = editor.isActive('table');

  return (
    <div
      className="rounded-lg border border-line bg-white focus-within:border-electric"
      // Genuine focus-leave (not a toolbar click, which stays inside this wrapper) commits the
      // edit — so clicking away saves without forcing the reviewer to hit Done first.
      onBlur={(e) => {
        // The link dialog is portalled outside this wrapper; treat it as still "inside".
        if (linkOpenRef.current) return;
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit();
      }}
    >
      {/* Sticky: the editor auto-sizes to the section, so a long section scrolled the toolbar
          out of view and left the reviewer with no way to style the text they were editing.
          Pins to the viewport top while this editor is on screen; its containing block is
          this wrapper, so it never escapes its own editor. NOTE: this only works because
          SectionCard uses `overflow-clip` rather than `overflow-hidden` — the latter makes
          the card a scroll container, which silently makes `sticky` inert on page scroll. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-lg border-b border-line-soft bg-white px-2 py-1.5">
        <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <BoldIcon className="size-4" />
        </ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <ItalicIcon className="size-4" />
        </ToolbarButton>
        <Divider />
        {/* Sub-headings only (H3/H4). The section itself is the H2 unit — the outline splits at
            every `## `, so a toolbar that could insert an H2 would fragment this section into two
            rows on reload. H3/H4 add structure INSIDE the section without crossing that boundary. */}
        <ToolbarButton
          title="Heading"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          title="Sub-heading"
          active={editor.isActive('heading', { level: 4 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        >
          <Heading4 className="size-4" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="size-4" />
        </ToolbarButton>
        <ToolbarButton title="Link" active={editor.isActive('link')} onClick={openLinkDialog}>
          <Link2 className="size-4" />
        </ToolbarButton>
        <Divider />
        <div className="relative">
          <ToolbarButton title="Insert table" active={tablePicker} onClick={() => setTablePicker((v) => !v)}>
            <TableIcon className="size-4" />
          </ToolbarButton>
          {tablePicker && (
            <TableSizePicker
              onPick={(rows, cols) => {
                editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                setTablePicker(false);
              }}
            />
          )}
        </div>
        {inTable && (
          <>
            <ToolbarButton title="Add row" onClick={() => editor.chain().focus().addRowAfter().run()}>
              <Rows3 className="size-4" />
            </ToolbarButton>
            <ToolbarButton title="Add column" onClick={() => editor.chain().focus().addColumnAfter().run()}>
              <Columns3 className="size-4" />
            </ToolbarButton>
            <ToolbarButton title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>
              <Trash2 className="size-4" />
            </ToolbarButton>
          </>
        )}
        <div className="ml-auto">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              commit();
              onDone();
            }}
            className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-navy/90"
          >
            <Check className="size-3.5" aria-hidden /> Done
          </button>
        </div>
      </div>
      <EditorContent editor={editor} />
      {/* Plain-language hint for reviewers who've never used a rich editor — the "select first"
          model isn't obvious, and the icon-only toolbar relies on hover tooltips otherwise. */}
      <p className="m-0 border-t border-line-soft px-3.5 py-1.5 text-[11.5px] text-muted">
        Select text, then choose a style above. Hover a button to see what it does.
      </p>

      <Dialog open={linkDraft !== null} onOpenChange={(open) => !open && closeLinkDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[15px]">Link address</DialogTitle>
            <DialogDescription className="text-[12.5px]">
              Select text first to turn it into a link, or add one here and the address becomes
              the link text.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              applyLink((linkDraft ?? '').trim());
            }}
          >
            <Input
              autoFocus
              value={linkDraft ?? ''}
              onChange={(e) => {
                setLinkDraft(e.target.value);
                setLinkError(null);
              }}
              placeholder="https://example.com"
              aria-label="Link address"
              aria-invalid={linkError != null}
            />
            {linkError && (
              <p className="mt-1.5 mb-0 text-[12px] text-state-error-text">{linkError}</p>
            )}
            <DialogFooter className="mt-4 gap-2">
              {editor.isActive('link') && (
                <button
                  type="button"
                  onClick={removeLink}
                  className="mr-auto rounded-md px-3 py-1.5 text-[12.5px] font-medium text-state-error-text hover:bg-state-error-bg"
                >
                  Remove link
                </button>
              )}
              <button
                type="button"
                onClick={closeLinkDialog}
                className="rounded-md border border-line px-3 py-1.5 text-[12.5px] font-medium text-navy hover:bg-line/30"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md bg-navy px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-navy/90"
              >
                Apply
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
